// ─── Sync conflict entity schemas ────────────────────────────────────────────
//
// Per-entity field schema + pure formatters used by the sync conflict
// resolution views. Keeps the presentation layer free of inline field labels,
// paise-to-rupee math, and ISO-to-date string parsing. All formatters are pure
// and independently unit-testable.
//
// Originally authored in apps/pos (G2.18). Moved to @bbpos/utils so both POS
// and Admin apps share a single source of truth.
//
// Traceability: FR-SYNC-013 (conflict resolution screen), US-BILL-019.

import { formatAmount } from './currency';

// ─── Formatters ──────────────────────────────────────────────────────────────

const DASH = '\u2014';

const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'Asia/Kolkata',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
  timeZone: 'Asia/Kolkata',
});

/**
 * Format a BIGINT minor-unit value as a currency-prefixed string. Accepts
 * number or numeric string (JSON payloads from the backend serialize BIGINT as
 * strings in some older paths — defensive).
 *
 * Currency-aware: the default (`'INR'`) keeps the legacy
 * `Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' })` output
 * byte-identical for every existing (INR) consumer. Any other in-scope currency
 * routes through `@bbpos/utils#formatAmount`, which derives the symbol, subunit
 * divisor and decimals from the currency (3-decimal GCC currencies — OMR/BHD/
 * KWD — render 3 decimals; no `/ 100` or `en-IN` INR assumption). Callers in
 * the conflict-diff UI thread the tenant currency in via `formatValue`.
 */
export function formatPaiseDecimal(value: unknown, currency = 'INR'): string {
  if (value === null || value === undefined || value === '') return DASH;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DASH;
  if (currency === 'INR') return inrFormatter.format(n / 100);
  return formatAmount(n, currency);
}

/** Format an ISO 8601 timestamp as `11 Apr 2026` (Asia/Kolkata). */
export function formatDate(value: unknown): string {
  if (value === null || value === undefined || value === '') return DASH;
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return DASH;
  return dateFormatter.format(d);
}

/** Format an ISO 8601 timestamp as `11 Apr 2026, 03:45 PM` (Asia/Kolkata). */
export function formatDateTime(value: unknown): string {
  if (value === null || value === undefined || value === '') return DASH;
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return DASH;
  return dateTimeFormatter.format(d);
}

/** Capitalize a raw enum value (e.g. `ACTIVE` -> `Active`). */
export function formatEnum(value: unknown): string {
  if (value === null || value === undefined || value === '') return DASH;
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** Format an E.164 phone number as `+91 98765 43210`. Falls back to raw. */
export function formatPhone(value: unknown): string {
  if (value === null || value === undefined || value === '') return DASH;
  const s = String(value).trim();

  // India is the dominant case in Orio360 — handle it explicitly first to
  // avoid greedy-regex ambiguity on the country code.
  const india = s.match(/^\+91(\d{10})$/);
  if (india) {
    const rest = india[1];
    return `+91 ${rest.slice(0, 5)} ${rest.slice(5)}`;
  }

  // Generic E.164 fallback: 1-3 digit country code, then 7-14 digit national
  // number. Regex is non-greedy on the country code segment — we match the
  // longest possible national number by requiring at least 7 trailing digits.
  const generic = s.match(/^\+(\d{1,3})(\d{7,14})$/);
  if (!generic) return s;
  const [, cc, rest] = generic;
  return `+${cc} ${rest.replace(/(\d{4})(?=\d)/g, '$1 ').trim()}`;
}

/** Boolean -> Yes / No. */
export function formatBool(value: unknown): string {
  if (value === null || value === undefined) return DASH;
  if (value === true || value === 'true') return 'Yes';
  if (value === false || value === 'false') return 'No';
  return DASH;
}

// ─── Schema types ────────────────────────────────────────────────────────────

export type FieldFormat =
  | 'currency'
  | 'date'
  | 'datetime'
  | 'enum'
  | 'phone'
  | 'bool'
  | 'plain';

export interface FieldSpec {
  key: string;
  label: string;
  format?: FieldFormat;
}

export interface EntitySchema {
  groups: Array<{ title: string; fields: FieldSpec[] }>;
}

// ─── Regime-aware schema labels ──────────────────────────────────────────────
//
// The BILL / QUOTATION / PRODUCT schemas carry India-only tax vocabulary
// ("Tax (GST)", "CGST/SGST/IGST", "GST Rate", "HSN Code"). Under a GCC VAT
// regime there is a single VAT line (no CGST/SGST/IGST split) and no
// HSN/SAC surface. Rather than hardcode the India strings, the tax-bearing
// schemas are built from a minimal regime-resolved label set threaded in by
// the caller (the shared SyncConflictViews pass their `taxRegime` prop).
//
// `@bbpos/utils` stays dependency-free: `SyncSchemaLabels` is a structural
// subset of `@bbpos/ui`'s `TaxRegimePresentation`, so a resolved regime can
// be passed straight in without an import. The default reproduces the legacy
// India vocabulary, so every existing consumer (and INR tenant) is
// byte-identical.

export interface SyncSchemaLabels {
  /** Short tax name: `GST` (India) · `VAT` (GCC) · `Tax` (no-VAT). */
  readonly taxName: string;
  /**
   * Per-rate split column labels. India ⇒ `['CGST','SGST','IGST']`; every GCC
   * regime is a single VAT line ⇒ `null` (one `taxName` total field).
   */
  readonly splitLabels: readonly string[] | null;
  /** India-only HSN/SAC surface — hidden for every GCC regime. */
  readonly showHsn: boolean;
}

/**
 * Default labels — the legacy India (GST) vocabulary. Keeps the exported
 * `*_SCHEMA` constants and any caller that omits labels byte-identical to the
 * pre-multi-currency behaviour.
 */
export const GST_INDIA_SCHEMA_LABELS: SyncSchemaLabels = {
  taxName: 'GST',
  splitLabels: ['CGST', 'SGST', 'IGST'],
  showHsn: true,
};

/**
 * Apply a field format to a raw value. Returns a display-ready string.
 *
 * `currency` (default `'INR'`) is threaded only into the `currency` format —
 * every existing caller that omits it stays byte-identical. The conflict-diff
 * UI (`FieldRow`) passes the tenant currency so non-INR conflicts render with
 * the correct symbol/subunit/decimals.
 */
export function formatValue(
  format: FieldFormat | undefined,
  value: unknown,
  currency = 'INR',
): string {
  switch (format) {
    case 'currency': return formatPaiseDecimal(value, currency);
    case 'date':     return formatDate(value);
    case 'datetime': return formatDateTime(value);
    case 'enum':     return formatEnum(value);
    case 'phone':    return formatPhone(value);
    case 'bool':     return formatBool(value);
    case 'plain':
    default:
      if (value === null || value === undefined || value === '') return DASH;
      return String(value);
  }
}

// ─── QUOTATION schema ────────────────────────────────────────────────────────

/**
 * QUOTATION schema, resolved against a tax regime. India ⇒ `Tax (GST)`
 * (byte-identical to the legacy label); GCC ⇒ `Tax (VAT)` / `Tax (Tax)`.
 */
export function getQuotationSchema(
  labels: SyncSchemaLabels = GST_INDIA_SCHEMA_LABELS,
): EntitySchema {
  return {
    groups: [
      {
        title: 'Header',
        fields: [
          { key: 'quote_number', label: 'Quote Number' },
          { key: 'status',       label: 'Status',     format: 'enum' },
          { key: 'valid_until',  label: 'Valid Until', format: 'date' },
          { key: 'created_at',   label: 'Created',    format: 'datetime' },
        ],
      },
      {
        title: 'Customer',
        fields: [
          { key: 'customer_name',  label: 'Name' },
          { key: 'customer_phone', label: 'Phone', format: 'phone' },
          { key: 'customer_id',    label: 'Customer ID' },
        ],
      },
      {
        title: 'Totals',
        fields: [
          { key: 'subtotal',       label: 'Subtotal',                   format: 'currency' },
          { key: 'tax_total',      label: `Tax (${labels.taxName})`,    format: 'currency' },
          { key: 'discount_total', label: 'Discount',                   format: 'currency' },
          { key: 'grand_total',    label: 'Grand Total',                format: 'currency' },
        ],
      },
      {
        title: 'Meta',
        fields: [
          { key: 'notes',          label: 'Notes' },
          { key: 'location_id',    label: 'Location ID' },
          { key: 'is_inter_state', label: 'Inter-state', format: 'bool' },
        ],
      },
    ],
  };
}

/** Default (India / GST) QUOTATION schema — byte-identical to the legacy constant. */
export const QUOTATION_SCHEMA: EntitySchema = getQuotationSchema();

/**
 * Shallow comparison of a single field value across two objects. Uses
 * JSON.stringify so nested shapes and null/undefined are handled uniformly.
 * Returns true when the two values differ.
 */
export function fieldChanged(
  key: string,
  client: Record<string, unknown>,
  server: Record<string, unknown>,
): boolean {
  const hasC = key in client;
  const hasS = key in server;
  if (!hasC && !hasS) return false;
  if (hasC !== hasS) return true;
  return JSON.stringify(client[key]) !== JSON.stringify(server[key]);
}

// ─── BILL schema ─────────────────────────────────────────────────────────────

/**
 * BILL schema, resolved against a tax regime. India keeps the
 * `CGST`/`SGST`/`IGST` split (byte-identical to the legacy constant); every
 * GCC regime collapses those three rows into a single `tax_total` row labelled
 * with the regime tax name (`VAT` / `Tax`).
 */
export function getBillSchema(
  labels: SyncSchemaLabels = GST_INDIA_SCHEMA_LABELS,
): EntitySchema {
  const taxFields: FieldSpec[] = labels.splitLabels
    ? [
        { key: 'cgst_total', label: labels.splitLabels[0], format: 'currency' },
        { key: 'sgst_total', label: labels.splitLabels[1], format: 'currency' },
        { key: 'igst_total', label: labels.splitLabels[2], format: 'currency' },
      ]
    : [{ key: 'tax_total', label: labels.taxName, format: 'currency' }];

  return {
    groups: [
      {
        title: 'Header',
        fields: [
          { key: 'bill_number', label: 'Bill Number' },
          { key: 'status',      label: 'Status',    format: 'enum' },
          { key: 'bill_date',   label: 'Bill Date',  format: 'date' },
          { key: 'created_at',  label: 'Created',   format: 'datetime' },
        ],
      },
      {
        title: 'Payment',
        fields: [
          { key: 'payment_method', label: 'Method',      format: 'enum' },
          { key: 'amount_paid',    label: 'Amount Paid', format: 'currency' },
          { key: 'grand_total',    label: 'Grand Total', format: 'currency' },
          { key: 'return_mode',    label: 'Return Mode', format: 'enum' },
        ],
      },
      {
        title: 'Totals',
        fields: [
          { key: 'subtotal',       label: 'Subtotal',       format: 'currency' },
          { key: 'discount_total', label: 'Discount',       format: 'currency' },
          ...taxFields,
        ],
      },
      {
        title: 'Meta',
        fields: [
          { key: 'reference_number', label: 'Reference No.' },
          { key: 'customer_id',      label: 'Customer ID' },
          { key: 'location_id',      label: 'Location ID' },
          { key: 'is_inter_state',   label: 'Inter-state', format: 'bool' },
          { key: 'notes',            label: 'Notes' },
        ],
      },
    ],
  };
}

/** Default (India / GST) BILL schema — byte-identical to the legacy constant. */
export const BILL_SCHEMA: EntitySchema = getBillSchema();

// ─── STOCK_ADJUSTMENT schema ────────────────────────────────────────────────

export const STOCK_ADJUSTMENT_SCHEMA: EntitySchema = {
  groups: [
    {
      title: 'Adjustment',
      fields: [
        { key: 'type',      label: 'Type',      format: 'enum' },
        { key: 'quantity',   label: 'Quantity' },
        { key: 'unit_cost',  label: 'Unit Cost', format: 'currency' },
        { key: 'notes',      label: 'Notes' },
      ],
    },
    {
      title: 'Product',
      fields: [
        { key: 'product_id', label: 'Product ID' },
        { key: 'name',       label: 'Name' },
        { key: 'sku',        label: 'SKU' },
      ],
    },
    {
      title: 'Meta',
      fields: [
        { key: 'created_at', label: 'Created', format: 'datetime' },
        { key: 'client_id',  label: 'Client ID' },
      ],
    },
  ],
};

// ─── PRODUCT schema ─────────────────────────────────────────────────────────

/**
 * PRODUCT schema, resolved against a tax regime. India ⇒ `GST Rate` + `HSN
 * Code` (byte-identical to the legacy constant); GCC ⇒ `VAT Rate` with the
 * HSN/SAC field omitted entirely (no GCC equivalent).
 */
export function getProductSchema(
  labels: SyncSchemaLabels = GST_INDIA_SCHEMA_LABELS,
): EntitySchema {
  const taxFields: FieldSpec[] = [
    { key: 'gst_rate', label: `${labels.taxName} Rate` },
    ...(labels.showHsn ? [{ key: 'hsn_code', label: 'HSN Code' } as FieldSpec] : []),
  ];

  return {
    groups: [
      {
        title: 'Basic Info',
        fields: [
          { key: 'name',     label: 'Name' },
          { key: 'sku',      label: 'SKU' },
          { key: 'barcode',  label: 'Barcode' },
          { key: 'category', label: 'Category' },
          { key: 'unit',     label: 'Unit' },
        ],
      },
      {
        title: 'Pricing',
        fields: [
          { key: 'price', label: 'Selling Price', format: 'currency' },
          { key: 'cost',  label: 'Cost Price',    format: 'currency' },
        ],
      },
      {
        title: 'Tax',
        fields: taxFields,
      },
      {
        title: 'Inventory',
        fields: [
          { key: 'stock_quantity', label: 'Stock Qty' },
          { key: 'is_active',     label: 'Active', format: 'bool' },
        ],
      },
      {
        title: 'Meta',
        fields: [
          { key: 'created_at', label: 'Created',  format: 'datetime' },
          { key: 'updated_at', label: 'Updated',  format: 'datetime' },
        ],
      },
    ],
  };
}

/** Default (India / GST) PRODUCT schema — byte-identical to the legacy constant. */
export const PRODUCT_SCHEMA: EntitySchema = getProductSchema();

// ─── EXPENSE schema ─────────────────────────────────────────────────────────

export const EXPENSE_SCHEMA: EntitySchema = {
  groups: [
    {
      title: 'Details',
      fields: [
        { key: 'description',  label: 'Description' },
        { key: 'category',     label: 'Category' },
        { key: 'amount',       label: 'Amount',       format: 'currency' },
        { key: 'expense_date', label: 'Expense Date', format: 'date' },
      ],
    },
    {
      title: 'Status',
      fields: [
        { key: 'status',      label: 'Status',    format: 'enum' },
        { key: 'reference',   label: 'Reference' },
        { key: 'receipt_url', label: 'Receipt' },
      ],
    },
    {
      title: 'Meta',
      fields: [
        { key: 'location_id', label: 'Location ID' },
        { key: 'created_at',  label: 'Created', format: 'datetime' },
        { key: 'updated_at',  label: 'Updated', format: 'datetime' },
      ],
    },
  ],
};

// ─── CUSTOMER schema ────────────────────────────────────────────────────────

export const CUSTOMER_SCHEMA: EntitySchema = {
  groups: [
    {
      title: 'Profile',
      fields: [
        { key: 'name',  label: 'Name' },
        { key: 'phone', label: 'Phone', format: 'phone' },
        { key: 'email', label: 'Email' },
      ],
    },
    {
      title: 'Personal',
      fields: [
        { key: 'date_of_birth', label: 'Date of Birth', format: 'date' },
        { key: 'anniversary',   label: 'Anniversary',   format: 'date' },
        { key: 'address',       label: 'Address' },
      ],
    },
    {
      title: 'Membership',
      fields: [
        { key: 'loyalty_points', label: 'Loyalty Points' },
        { key: 'is_active',      label: 'Active', format: 'bool' },
      ],
    },
    {
      title: 'Meta',
      fields: [
        { key: 'notes',      label: 'Notes' },
        { key: 'created_at', label: 'Created', format: 'datetime' },
      ],
    },
  ],
};
